import React from "react";

// Payload Lexical JSON → React nodes.
// Supports: root/paragraph/heading/text/linebreak/link/list/listitem/quote
// + Payload block nodes with `blockType` (branded blocks from Sprint 1-I).
// Kept intentionally simple — matches only the node shapes we actually
// emit from SEO Studio's article generator.

const FORMAT_BOLD = 1 << 0;
const FORMAT_ITALIC = 1 << 1;
const FORMAT_STRIKE = 1 << 2;
const FORMAT_UNDER = 1 << 3;
const FORMAT_CODE = 1 << 4;

// Match [[IMAGE ...]] placeholders — both closed `]]` and unclosed
// variants a truncated generation may leave behind. Defence-in-depth:
// backend hydrate + DB cleanup strip these too, this is the last stop.
const IMAGE_PLACEHOLDER_RE = /\s*\[\[IMAGE\b(?:[^\[\]]|\][^\]])*(?:\]\]|$)/gi;

function renderText(node, i) {
  const fmt = node.format || 0;
  let el = (node.text || "").replace(IMAGE_PLACEHOLDER_RE, "").trim();
  if (!el) return null;
  const wrappers = [];
  if (fmt & FORMAT_BOLD) wrappers.push("strong");
  if (fmt & FORMAT_ITALIC) wrappers.push("em");
  if (fmt & FORMAT_UNDER) wrappers.push("u");
  if (fmt & FORMAT_STRIKE) wrappers.push("s");
  if (fmt & FORMAT_CODE) wrappers.push("code");
  let out = <React.Fragment key={i}>{el}</React.Fragment>;
  for (const w of wrappers) {
    out = React.createElement(w, { key: `${i}-${w}` }, out);
  }
  return out;
}

function renderChildren(children) {
  if (!Array.isArray(children)) return null;
  return children.map((n, i) => renderNode(n, i));
}

// Recursively concatenate the visible text of a Lexical subtree.
function _textOf(nodes) {
  if (!nodes) return "";
  if (typeof nodes === "string") return nodes;
  if (Array.isArray(nodes)) return nodes.map(_textOf).join("");
  if (nodes.type === "text" && typeof nodes.text === "string") return nodes.text;
  return _textOf(nodes.children);
}

// Same slug logic as TableOfContents.jsx — keep them in lockstep.
function _slugifyHeading(text) {
  return String(text || "")
    .toLowerCase()
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9\s-]/g, "")
    .trim()
    .replace(/\s+/g, "-")
    .slice(0, 80);
}

function renderNode(node, i) {
  if (!node || typeof node !== "object") return null;
  switch (node.type) {
    case "text":
      return renderText(node, i);
    case "linebreak":
      return <br key={i} />;
    case "heading": {
      // The article page already renders <h1>{title}</h1> in the header.
      // If the LLM output starts with an <h1> (which it usually does — the
      // same title as a first line), demote it to <h2> to avoid a duplicate
      // H1 on the page (SEO violation). Any subsequent stray h1 inside the
      // body is also demoted.
      let tag = node.tag || "h2";
      if (tag === "h1") tag = "h2";
      // Emit stable `id` slugs on H2/H3 so the TableOfContents anchor
      // links resolve (matches slugifyHeading() in TableOfContents.jsx).
      const props = { key: i, className: `blog-${tag}` };
      if (tag === "h2" || tag === "h3") {
        const text = _textOf(node.children);
        const id = _slugifyHeading(text);
        if (id) props.id = id;
      }
      return React.createElement(tag, props, renderChildren(node.children));
    }
    case "paragraph": {
      // LLMs frequently emit horizontal rules as a paragraph containing
      // only "---" (or "***" / "___"). Render those as a real <hr> so
      // they act as section breaks instead of stray literal text.
      const kids = Array.isArray(node.children) ? node.children : [];
      if (kids.length === 1 && kids[0]?.type === "text") {
        const only = (kids[0].text || "").trim();
        if (/^(-{3,}|\*{3,}|_{3,})$/.test(only)) {
          return <hr key={i} className="blog-hr" />;
        }
      }
      return (
        <p key={i} className="blog-p">{renderChildren(node.children)}</p>
      );
    }
    case "quote":
      return (
        <blockquote key={i} className="blog-quote">
          {renderChildren(node.children)}
        </blockquote>
      );
    case "list": {
      const Tag = node.listType === "number" ? "ol" : "ul";
      return React.createElement(
        Tag, { key: i, className: `blog-${Tag}` },
        renderChildren(node.children),
      );
    }
    case "listitem":
      return <li key={i} className="blog-li">{renderChildren(node.children)}</li>;
    case "link": {
      const url = node.fields?.url || node.url || "#";
      const rel = /^https?:\/\//i.test(url) ? "noopener noreferrer" : undefined;
      const target = /^https?:\/\//i.test(url) ? "_blank" : undefined;
      return (
        <a key={i} href={url} rel={rel} target={target} className="blog-link">
          {renderChildren(node.children)}
        </a>
      );
    }
    case "horizontalrule":
    case "hr":
      return <hr key={i} className="blog-hr" />;
    case "block":
      return <BlockNode key={i} block={node.fields || {}} />;
    default:
      // Fallback: render children if present
      if (Array.isArray(node.children)) return renderChildren(node.children);
      return null;
  }
}

// ── Branded block renderers ────────────────────────────────────────
function BlockNode({ block }) {
  const bt = block.blockType;
  switch (bt) {
    case "imageBlock":
      return <ImageBlock {...block} />;
    case "noteBlock":
      return <CalloutBlock kind="note" {...block} />;
    case "warningBlock":
      return <CalloutBlock kind="warning" {...block} />;
    case "tipBlock":
      return <CalloutBlock kind="tip" {...block} />;
    case "checklistBlock":
      return <ChecklistBlock {...block} />;
    case "ctaBlock":
      return <CtaBlock {...block} />;
    case "comparisonBlock":
      return <ComparisonBlock {...block} />;
    default:
      return null;
  }
}

function ImageBlock({ image, caption, aspect }) {
  // Payload stores media relations as either a populated object (has .url)
  // or a raw ObjectId string when depth is 0. A 24-char hex ObjectId is
  // NOT a valid image URL — silently hide the block rather than render a
  // broken <img> that shows the browser's question-mark placeholder.
  const url = _resolveImageUrl(image);
  const [failed, setFailed] = React.useState(false);
  if (!url || failed) return null;
  const alt = (typeof image === "object" && image?.alt) || caption || "";
  const aspectClass = {
    "16-9": "aspect-video",
    "4-3": "aspect-[4/3]",
    "1-1": "aspect-square",
  }[aspect] || "aspect-video";
  return (
    <figure className="blog-figure my-10">
      <div className={`${aspectClass} overflow-hidden rounded-lg bg-neutral-100`}>
        <img
          src={url}
          alt={alt}
          className="w-full h-full object-cover"
          loading="lazy"
          onError={() => setFailed(true)}
        />
      </div>
      {caption && (
        <figcaption className="text-sm text-neutral-500 mt-2 text-center">{caption}</figcaption>
      )}
    </figure>
  );
}

/** Return a real image URL from a Payload media reference — accepts:
 *   • { url: "https://..." | "/path" }         → returns url
 *   • { filename: "x.jpg" }                    → returns "/media/x.jpg" fallback
 *   • "https://..." | "/media/..."             → returns as-is
 *   • "6a4bea3145dc5c7ff9058b84" (raw ObjectId) → returns null (hide)
 *   • null / undefined / "" / non-string       → returns null (hide) */
function _resolveImageUrl(image) {
  if (!image) return null;
  if (typeof image === "object") {
    const url = image.url || image.URL;
    if (typeof url === "string" && url.trim()) return url;
    if (typeof image.filename === "string" && image.filename.includes(".")) {
      return `/media/${image.filename}`;
    }
    return null;
  }
  if (typeof image !== "string") return null;
  const s = image.trim();
  if (!s) return null;
  // ObjectId-shaped strings (24 hex chars) are internal DB refs, not URLs.
  if (/^[a-f0-9]{24}$/i.test(s)) return null;
  // Anything containing a path separator, a scheme, or a dot is treated
  // as a URL (relative or absolute).
  if (s.includes("/") || s.includes("://") || s.includes(".")) return s;
  return null;
}

const CALLOUT_STYLES = {
  note: { bg: "bg-blue-50", border: "border-blue-200", head: "text-blue-800", body: "text-blue-900", icon: "ℹ" },
  warning: { bg: "bg-amber-50", border: "border-amber-200", head: "text-amber-800", body: "text-amber-900", icon: "!" },
  tip: { bg: "bg-emerald-50", border: "border-emerald-200", head: "text-emerald-800", body: "text-emerald-900", icon: "✓" },
};

function CalloutBlock({ kind, title, body }) {
  const s = CALLOUT_STYLES[kind] || CALLOUT_STYLES.note;
  return (
    <aside className={`my-8 border-l-4 ${s.border} ${s.bg} p-4 rounded-r`}>
      <div className={`flex items-start gap-3`}>
        <div className={`text-lg ${s.head} font-mono`}>{s.icon}</div>
        <div className="min-w-0">
          {title && <div className={`font-semibold ${s.head} mb-1`}>{title}</div>}
          <div className={`text-sm ${s.body} whitespace-pre-wrap`}>{body}</div>
        </div>
      </div>
    </aside>
  );
}

function ChecklistBlock({ title, items }) {
  if (!Array.isArray(items) || items.length === 0) return null;
  return (
    <div className="my-8 border border-neutral-200 rounded-lg p-5 bg-neutral-50">
      {title && <div className="font-semibold text-neutral-900 mb-3">{title}</div>}
      <ul className="space-y-2">
        {items.map((it, i) => (
          <li key={i} className="flex items-start gap-2 text-neutral-800">
            <span className="text-emerald-700 font-bold leading-6">✓</span>
            <span>{it.text || it}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function CtaBlock({ title, body, buttonLabel, buttonHref }) {
  return (
    <div className="my-10 border-2 border-neutral-900 rounded-lg p-6 bg-white text-neutral-900">
      <div className="text-xl font-semibold mb-1">{title}</div>
      {body && <p className="text-neutral-700 mb-4">{body}</p>}
      {buttonLabel && (
        <a href={buttonHref || "#"} className="inline-flex items-center gap-2 bg-neutral-900 text-white rounded px-4 py-2 text-sm font-medium hover:bg-neutral-800">
          {buttonLabel} →
        </a>
      )}
    </div>
  );
}

function ComparisonBlock({ title, columns, rows }) {
  if (!Array.isArray(columns) || !Array.isArray(rows) || rows.length === 0) return null;
  return (
    <div className="my-8 overflow-x-auto">
      {title && <div className="font-semibold text-neutral-900 mb-2">{title}</div>}
      <table className="w-full border-collapse text-sm">
        <thead>
          <tr>
            {columns.map((c, i) => (
              <th key={i} className="border border-neutral-300 bg-neutral-100 text-left px-3 py-2 font-semibold text-neutral-900">
                {c.label || c}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} className={i % 2 === 0 ? "bg-white" : "bg-neutral-50"}>
              {(r.cells || []).map((cell, j) => (
                <td key={j} className="border border-neutral-300 px-3 py-2 text-neutral-800">
                  {cell.text || cell}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ── Public API ─────────────────────────────────────────────────────
export default function LexicalRenderer({ content }) {
  const root = content?.root;
  if (!root) return null;
  const children = Array.isArray(root.children) ? root.children : [];
  // LLM generators very often emit the article title as the first
  // heading node in the body ("# The Same Title"). The article page
  // already renders <h1>{article.title}</h1> in its header, so this
  // duplicate is redundant and harms readability. Skip the first
  // node if it's a heading — the article page owns the title.
  const start = children[0]?.type === "heading" ? 1 : 0;
  return (
    <div className="blog-content max-w-none">
      {children.slice(start).map((n, i) => renderNode(n, i))}
    </div>
  );
}
